import {
  panel,
  panelAction,
  panelActionCss,
  panelCss,
  positive,
} from '../armKit';
import { searchPath } from '../destinations';
import { fieldFeedWorks } from '../pageData';
import { topInterest, viewedUser } from '../profileFacts';
import { INK, WARM_HAIRLINE, worksTotal } from '../surface';

import type { ArmContext, ArmSpec } from '../armRegistry';
import type { FeedPaper } from '../pageData';

const DESK_SIZE = 3;
const MAX_NAMES = 3;

const ROWS_CLASS = 'profile-liautotest-fielddesk__rows';
const AUTHORS_CLASS = 'profile-liautotest-fielddesk__authors';
const PAPER_CLASS = 'profile-liautotest-fielddesk__paper';

const PAPER_SOURCE = 'profile-liautotest-field-desk-paper';
const SEARCH_SOURCE = 'profile-liautotest-field-desk-search';

// The title anchor borrows the control work card's Georgia serif so each row
// reads as a compacted card; :not(:hover) keeps it ink at rest while letting
// the page's own link color through on hover.
function deskCss(scope: string): string {
  return `
${scope} .${ROWS_CLASS} {
  list-style: none;
  margin: 0;
  padding: 0;
}
${scope} .${ROWS_CLASS} li {
  padding: 10px 0;
  border-top: 1px solid ${WARM_HAIRLINE};
}
${scope} .${ROWS_CLASS} li:first-child {
  border-top: 0;
}
${scope} .${AUTHORS_CLASS} {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: ${INK};
}
${scope} .${PAPER_CLASS} {
  display: block;
  font-family: Georgia, serif;
  font-size: 17px;
  line-height: 1.3;
}
${scope} .${PAPER_CLASS}:not(:hover) {
  color: ${INK};
}
`;
}

function authorLine(names: string[]): string {
  if (names.length <= MAX_NAMES) return names.join(', ');

  return `${names.slice(0, MAX_NAMES).join(', ')} et al.`;
}

function paperRow(paper: FeedPaper, ctx: ArmContext): HTMLLIElement {
  const item = document.createElement('li');

  // The authors lead every row by design; a paper the feed names no authors
  // for gets no line at all rather than a guessed one.
  if (paper.authorNames.length > 0) {
    const authors = document.createElement('span');
    authors.className = AUTHORS_CLASS;
    authors.textContent = authorLine(paper.authorNames);
    item.appendChild(authors);
  }

  const title = document.createElement('a');
  title.className = PAPER_CLASS;
  title.href = paper.url;
  title.textContent = paper.title;
  title.addEventListener('click', () => {
    ctx.track(PAPER_SOURCE, { entityId: paper.id });
  });
  item.appendChild(title);

  return item;
}

export const emptyProfileFieldDesk: ArmSpec = {
  // stamp deliberately unset: the zero-card gate is knowable pre-render, but
  // the feed fetch decides whether anything mounts, and a prediction the
  // deadline can veto is not a prediction — markRendered reports the truth.
  onReady: async (ctx) => {
    if (ctx.cards().length !== 0) return;

    // cards() counts only WORK strips, and a videos- or courses-only profile
    // renders zero of those while still showing content — where "hasn't
    // uploaded anything yet" would sit above the visible strips as a false
    // claim. Those profiles carry counted section chips, so a positive chip
    // total is the veto; a truly empty profile renders no counted chip at all.
    if (positive(worksTotal()) != null) return;

    const interest = topInterest();
    if (interest == null) return;

    const firstName = viewedUser()?.first_name?.trim() ?? '';
    if (firstName === '') return;

    const papers = (await fieldFeedWorks(interest.id))
      .filter((paper) => !paper.authorIds.includes(ctx.viewedUserId))
      .slice(0, DESK_SIZE);
    if (papers.length === 0) return;

    const slot = ctx.mountSlot('columnTop');
    if (slot == null) return;

    ctx.injectStyles(
      panelCss(ctx.scope, { frame: 'warm', marginBottom: '0' }) +
        panelActionCss(ctx.scope) +
        deskCss(ctx.scope),
    );

    const { root, body } = panel({
      title: `${firstName} hasn't uploaded anything yet`,
      subtitle: `Work by other researchers in ${interest.name}`,
    });

    const rows = document.createElement('ul');
    rows.className = ROWS_CLASS;
    papers.forEach((paper) => rows.appendChild(paperRow(paper, ctx)));
    body.appendChild(rows);

    body.appendChild(
      panelAction(`Search ${interest.name}`, searchPath(interest.name), () => {
        ctx.track(SEARCH_SOURCE);
      }),
    );

    // Above the page's own empty-state block, which stays exactly as served.
    slot.appendChild(root);
    ctx.markRendered();
  },
};
